import { useContext, useState, useEffect } from 'react';
import { numberWithCommas } from '../../../utils/format';
import AdjustmentsModal from '../../Adjustments/AdjustmentsModal';
import SimpleTable from '../../SimpleTable';
import { StoreContext } from '../../Store';
import { QAStoreContext } from './QAStore';
import { getValueFromID } from './utils';
import CustomsMoPConfirmationModal from './modals/CustomsMoPConfirmationModal';
import { VATMessageEnum } from '../../../utils/enums';
import { useDropdownOptions } from '../queryHooks/useDropdownOptions';

const EORI_DEFERMENT_NUMBER = '9093924';

export const calculateMopModalValue = ({
  hasAnyLiability,
  isBusinessPartner,
  hasDefermentAccount,
  hasCashAccount,
  isGbImport,
  isFfd,
  isEuImport,
  ffdMop,
  cashAccountNumber,
  defermentNumber,
  customsMoPConfirmed,
}) => {
  const mopValues = {
    eoriDeferment: {
      value: 'EORI Deferment',
      copyValue: EORI_DEFERMENT_NUMBER,
      isClickable: true,
      error: !customsMoPConfirmed,
    },
    ownDeferment: {
      value: 'Own Deferment',
      copyValue: defermentNumber,
      isClickable: false,
      error: false,
      showErrorIcon: false,
    },
    eoriTan: {
      value: 'EORI TAN',
      copyValue: EORI_DEFERMENT_NUMBER,
      isClickable: true,
      error: !customsMoPConfirmed,
    },
    ownTan: {
      value: 'Own TAN',
      copyValue: defermentNumber,
      isClickable: false,
      error: false,
      showErrorIcon: false,
    },
    invalid: {
      value: '',
      error: true,
    },
    notApplicable: {
      value: 'N/A',
      error: false,
    },
    ffdError: {
      value: 'EORI Deferment',
      copyValue: defermentNumber,
      isClickable: true,
      error: !customsMoPConfirmed,
      errorMessage: 'This shipment contains payments which are to be made via cash account. Please select FFD.',
      showErrorIcon: true,
    },
    cashAccount: {
      value: 'Cash Account',
      copyValue: cashAccountNumber,
      error: false,
    },
    select: {
      value: 'Select',
      copyValue: false,
      error: true,
    },
    checkWithCustomer: {
      value: 'Check with customer',
      isClickable: true,
      copyValue: false,
      error: !customsMoPConfirmed,
    },
  };

  const {
    notApplicable,
    invalid,
    ownDeferment,
    ffdError,
    cashAccount,
    select,
    checkWithCustomer,
    eoriDeferment,
    ownTan,
    eoriTan,
  } = mopValues;

  if (!hasAnyLiability) {
    return notApplicable;
  }

  if (!isBusinessPartner) {
    return invalid;
  }

  if (isEuImport) {
    if (hasDefermentAccount) {
      return ownTan;
    } else {
      return eoriTan;
    }
  }

  if (isGbImport) {
    if (hasDefermentAccount && hasCashAccount) {
      return ownDeferment;
    } else if (hasDefermentAccount) {
      return ownDeferment;
    } else if (hasCashAccount) {
      return ffdError;
    } else {
      return eoriDeferment;
    }
  }

  if (isFfd) {
    if (hasDefermentAccount && hasCashAccount) {
      if (ffdMop === '') {
        return select;
      } else if (ffdMop === 'importers deferment') {
        return ownDeferment;
      } else if (ffdMop === 'importers cash account') {
        return cashAccount;
      } else if (ffdMop === 'check with customer') {
        return checkWithCustomer;
      }
    } else if (hasDefermentAccount) {
      return ownDeferment;
    } else if (hasCashAccount) {
      return cashAccount;
    } else {
      return eoriDeferment;
    }
  }

  return {};
};

const FinancialsTable = ({ setIsFinancialsValid, incotermsData, readOnly = false }) => {
  const [showAdjustmentsModal, setShowAdjustmentsModal] = useState(false);
  const [customsMoPModalType, setCustomsMoPModalType] = useState(null);
  const [tableData, setTableData] = useState({
    headings: [],
    values: [],
  });
  const store = useContext(StoreContext);
  const invoiceCurrencyID = store.invoicecurr[0];
  const adjustments = store.adjustments[0];

  const { staticDropdowns } = useDropdownOptions();
  const { currencies } = staticDropdowns.data || {};

  const qaStore = useContext(QAStoreContext);
  const config = qaStore.currentConfig[0];
  const newLoadQaVat = qaStore.newLoadQaVat[0];
  const isPva = newLoadQaVat?.vatMessage === 'PVA';
  const [itemTotals] = qaStore.itemTotals;
  const { dutyLiabilityGbp, vatLiabilityGbp } = itemTotals;
  const [customsMoPConfirmation, setCustomsMoPConfirmation] = store.customsMoPConfirmation;

  const useTanAccount = store.QAConsigneeData[0]?.eori?.startsWith('IE') && config.documents.hasEUImportEntry;

  const validInvoiceCurrency = invoiceCurrencyID && currencies;
  const showAdjustmentsLink = adjustments?.length > 0;
  const adjustmentsRequired = adjustments?.some(
    (item) => item.required && (item.amount === '' || item.currency === '')
  );
  const adjustmentsInvalid = adjustmentsRequired && incotermsData?.doesIncotermRequireFreightCharge;

  useEffect(() => {
    //get value and error for customsMoP
    const customsMoPData = getMoPValue();
    store.customsMopValue[1](customsMoPData.value);
    qaStore.confirmDutyRequired[1](customsMoPData.isClickable && customsMoPData.error);

    const tableValues = {
      id: 'financials-table-values',
      invoiceCurrency: validInvoiceCurrency && getValueFromID(currencies, invoiceCurrencyID),
      incoterms: incotermsData.validIncoterm && incotermsData.incoTermText,
      adjustments: showAdjustmentsLink && (
        <button
          className="button-text-link"
          onClick={() => {
            setShowAdjustmentsModal(true);
          }}
          title="Click to view all adjustments"
        >
          View
        </button>
      ),
      potentialLiability: store.invoicecurr[0] ? qaStore.potentialLiabilityGBP[0] : '',
      customsMop: customsMoPData.isClickable ? (
        <button
          data-testid="qa-financials-eori-deferment-link"
          className={`button-text-link ${customsMoPData.error ? 'text-danger' : ''}`}
          onClick={() => setCustomsMoPModalType(customsMoPData.value === 'Check with customer' ? 'check' : 'eori')}
          title="Click to confirm EORI Deferment usage"
        >
          {customsMoPData.value}
        </button>
      ) : (
        <span data-testid="qa-financials-eori-deferment-link" className={customsMoPData.error ? 'text-danger' : ''}>
          {customsMoPData.value}
        </span>
      ),
      vat: newLoadQaVat.vatMessage === VATMessageEnum.CustomsMOP ? customsMoPData.value : newLoadQaVat.vatMessage,
    };

    // errors should only be set to true if the fields themselves are visible too
    const incotermError = !incotermsData.validIncoterm;
    const adjustmentsError = config?.visibleFields?.adjustments && (adjustmentsInvalid || !incotermsData.validIncoterm);
    const mopError = config?.visibleFields?.customsMop && !!customsMoPData.error;
    const vatError = config?.visibleFields?.vat && !newLoadQaVat.vatValid;

    const financialsFieldsValid = !(incotermError || adjustmentsError || mopError || vatError) && validInvoiceCurrency;
    setIsFinancialsValid(financialsFieldsValid || !config.visibleFields.financialsTable);

    setTableData({
      headings: [
        {
          label: 'Invoice Currency',
          key: 'invoiceCurrency',
          error: !validInvoiceCurrency,
          valueCellId: 'qa-financials-invoice-currency-value',
        },
        {
          label: 'Incoterms',
          key: 'incoterms',
          error: incotermError,
          valueCellId: 'qa-financials-incoterms-value',
        },
        {
          label: 'Adjustments',
          key: 'adjustments',
          error: adjustmentsError,
          hide: !config?.visibleFields?.adjustments,
          valueCellId: 'qa-financials-adjustments-value',
        },
        {
          label: 'Potential Liability (GBP)',
          key: 'potentialLiability',
          hide: !config?.visibleFields?.potentialLiability,
          valueCellId: 'qa-financials-potential-liability-value',
        },
        {
          label: 'Customs MoP',
          key: 'customsMop',
          error: mopError,
          hide: !config?.visibleFields?.customsMop,
          copyValue: customsMoPData?.copyValue,
          copyButtonId: 'qa-financials-customs-mop-copy-button',
          showErrorIcon: customsMoPData?.showErrorIcon,
          errorMessage: customsMoPData?.errorMessage,
          valueCellId: 'qa-financials-customs-mop-value',
        },
        {
          label: 'VAT',
          key: 'vat',
          hide: !config?.visibleFields?.vat,
          error: vatError,
          valueCellId: 'qa-financials-vat-value',
        },
      ],
      values: [tableValues],
    });

    getPotentialLiability();
  }, [
    validInvoiceCurrency,
    incotermsData,
    newLoadQaVat,
    currencies,
    invoiceCurrencyID,
    showAdjustmentsLink,
    qaStore.potentialLiabilityGBP,
    adjustmentsInvalid,
    config,
    store?.QAConsigneeData,
    customsMoPConfirmation,
    store.dutyRates[0],
    store.QACustomerData[0],
    dutyLiabilityGbp,
  ]);

  const handleMoPConfirmation = (selectedOption) => {
    setCustomsMoPConfirmation({
      confirmed: true,
      selection: selectedOption,
    });
    setCustomsMoPModalType(null);
  };

  const getMoPValue = () => {
    const mopLookupInfo = store.representative[0] ? store.QACustomerData[0] : store.QAConsigneeData[0];
    // Consignee or Customer MoP Data
    const isBusinessPartner = !!store.QAConsigneeData[0].cabieStatus; // always check consignee for now
    const hasDefermentAccount = !!mopLookupInfo?.defermentNumber;
    const hasCashAccount = !!mopLookupInfo?.cashAccountNumber;
    const ffdMop = mopLookupInfo?.ffdMoP?.toLowerCase();

    // Item data
    const hasPotentialLiability = qaStore.potentialLiabilityGBP[0] === '-' ? false : !!qaStore.potentialLiabilityGBP[0];
    const itemsWithExciseOrDuty = store.dutyRates[0]?.some(({ excise, additionalDuties, dutyLiability }) => {
      return excise || additionalDuties || dutyLiability === 'CALCULATION';
    });
    const hasAnyLiability = hasPotentialLiability || itemsWithExciseOrDuty;

    // Order Form data
    const isGbImport = config?.documents?.hasGBImport;
    const isFfd = config?.documents?.hasFFD;
    const isEuImport = config?.documents?.hasEUImportEntry;

    //Customs MoP modal data
    const customsMoPConfirmed = !!customsMoPConfirmation?.confirmed;

    return calculateMopModalValue({
      isBusinessPartner,
      hasAnyLiability,
      hasDefermentAccount,
      hasCashAccount,
      hasPotentialLiability,
      itemsWithExciseOrDuty,
      isGbImport,
      isFfd,
      isEuImport,
      ffdMop,
      defermentNumber: mopLookupInfo?.defermentNumber,
      cashAccountNumber: mopLookupInfo?.cashAccountNumber,
      customsMoPConfirmed,
    });
  };

  const getPotentialLiability = () => {
    const potentialLiability = isPva ? dutyLiabilityGbp : dutyLiabilityGbp + vatLiabilityGbp;
    qaStore.potentialLiabilityGBP[1](potentialLiability ? numberWithCommas(potentialLiability, true) : '-');
  };

  return (
    <>
      <AdjustmentsModal
        adjustments={store.adjustments[0]}
        setAdjustments={store.adjustments[1]}
        currencyOptions={currencies}
        show={showAdjustmentsModal}
        onHide={() => {
          setShowAdjustmentsModal(false);
        }}
        readOnly
      />
      <CustomsMoPConfirmationModal
        onHide={() => setCustomsMoPModalType(null)}
        onConfirm={handleMoPConfirmation}
        initialSelection={customsMoPConfirmation?.selection ?? null}
        readOnly={readOnly}
        useTanAccount={useTanAccount}
        type={customsMoPModalType}
      />
      <SimpleTable data={tableData} isHeaderRow={false} />
    </>
  );
};

export default FinancialsTable;
